/**
 * TPRA Home Findings Summary API Request Hook
 * Fetches findings summary data for the donut chart visualization
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraHomeQueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { FindingsSummaryResponse } from "../types";
import { FindingsSummaryResponseSchema } from "../validation/schemas";

/**
 * Fetches findings summary data from the API
 * @returns Validated findings summary response with severity breakdowns
 * @throws Error if API request fails or response validation fails
 */
async function fetchFindingsSummary(): Promise<FindingsSummaryResponse> {
  const response = await get<FindingsSummaryResponse>({
    url: APIEndpoints.TPRAHomeFindingsSummary,
  });

  // Validate response with Zod schema
  const validated = FindingsSummaryResponseSchema.parse(response);

  return validated;
}

/**
 * React Query hook for fetching TPRA findings summary
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 *
 * @returns React Query result with findings summary data
 *
 * @example
 * ```typescript
 * function FindingsSummary() {
 *   const { data, isLoading, isError, refetch } = useGetFindingsSummary();
 *
 *   if (isLoading) return <FindingsSummarySkeleton />;
 *   if (isError) return <SectionError onRetry={refetch} />;
 *
 *   return <DonutChart data={data} />;
 * }
 * ```
 */
export function useGetFindingsSummary(): UseQueryResult<
  FindingsSummaryResponse,
  Error
> {
  return useQuery({
    queryKey: tpraHomeQueryKeys.findings(),
    queryFn: fetchFindingsSummary,

    // Caching Strategy
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention

    // Retry Strategy (exponential backoff)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

    // Refetch Behavior
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
